import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Grid, 
  Chip,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Avatar,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  DateRange as DateRangeIcon,
  AttachMoney as AttachMoneyIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  Notes as NotesIcon,
  Add as AddIcon,
  Send as SendIcon,
  LocationOn as LocationOnIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
  Receipt as ReceiptIcon,
  ChecklistRtl as ChecklistIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import ReadOnlyValueField from '../components/ReadOnlyValueField';
import GoogleMap from '../components/GoogleMap';
import commercialVisitService from '../services/commercialVisit.service';
import costService from '../services/cost.service';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';
import { 
  CommercialVisitType, 
  COMMERCIAL_VISIT_TYPE_LABELS, 
  // @ts-ignore - VisitStatus não existe no módulo
  VisitStatus, 
  COMMERCIAL_VISIT_OUTCOME_LABELS,
  COMMERCIAL_VISIT_STATUS_LABELS,
  COMMERCIAL_VISIT_PRIORITY_LABELS
} from '../types/commercialVisit.types';
import { CostType, COST_CATEGORY_LABELS } from '../types/cost.types';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/AuthLayout';

const CommercialVisitDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { showSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  
  const [visit, setVisit] = useState<CommercialVisitType | null>(null);
  const [costs, setCosts] = useState<CostType[]>([]);
  const [totalCost, setTotalCost] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingCosts, setLoadingCosts] = useState<boolean>(false);
  
  // Estado para controlar a aba ativa
  const [activeTab, setActiveTab] = useState<number>(0);
  
  // Estado para diálogo de exclusão
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  
  // Estado para diálogo de adição de ação
  const [actionDialogOpen, setActionDialogOpen] = useState<boolean>(false);
  const [newAction, setNewAction] = useState<string>('');
  const [addingAction, setAddingAction] = useState<boolean>(false);
  
  // Carregar detalhes da visita
  useEffect(() => {
    const loadVisit = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const visitData = await commercialVisitService.getCommercialVisitById(id);
        setVisit(visitData);
      } catch (error) {
        console.error('Erro ao carregar visita comercial:', error);
        showSnackbar('Erro ao carregar detalhes da visita comercial. Tente novamente.', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    loadVisit();
  }, [id]);
  
  // Carregar custos relacionados à visita
  useEffect(() => {
    const loadCosts = async () => {
      if (!id) return;
      
      console.log('CommercialVisitDetail: Iniciando carregamento de custos para visita ID:', id);
      
      try {
        setLoadingCosts(true);
        const response = await costService.getCostsByCommercialVisit(id);
        console.log('CommercialVisitDetail: Resposta da API de custos:', response);
        
        if (response && Array.isArray(response.costs)) {
          // Garantir que temos os custos e calcular o total manualmente
          const costsArray = response.costs;
          
          // Calcular o total manualmente somando todos os custos
          const calculatedTotal = costsArray.reduce((sum, cost) => sum + (cost.amount || 0), 0);
          
          console.log(`CommercialVisitDetail: ${costsArray.length} custos encontrados, total calculado: ${calculatedTotal}`);
          
          setCosts(costsArray);
          // Usar o total calculado manualmente para garantir precisão
          setTotalCost(calculatedTotal);
        } else {
          console.warn('CommercialVisitDetail: Resposta vazia ou em formato inesperado');
          setCosts([]);
          setTotalCost(0);
        }
      } catch (error) {
        console.error('Erro ao carregar custos da visita:', error);
        setCosts([]);
        setTotalCost(0);
      } finally {
        setLoadingCosts(false);
      }
    };
    
    loadCosts();
  }, [id]);
  
  // Preparar dados para o gráfico de distribuição de custos
  const prepareCostDistributionData = () => {
    if (!costs || costs.length === 0) {
      return [];
    }
    
    // Agrupar custos por categoria
    const categoryTotals: { [key: string]: number } = {};
    
    costs.forEach(cost => {
      const category = cost.category || 'other';
      if (!categoryTotals[category]) {
        categoryTotals[category] = 0;
      }
      categoryTotals[category] += cost.amount || 0;
    });
    
    // Converter para formato do gráfico
    const data = Object.keys(categoryTotals).map(category => ({
      name: COST_CATEGORY_LABELS[category as keyof typeof COST_CATEGORY_LABELS] || category,
      value: categoryTotals[category],
      percentage: totalCost > 0 ? ((categoryTotals[category] / totalCost) * 100).toFixed(1) : 0
    }));
    
    // Ordenar por valor (maior para menor)
    return data.sort((a, b) => b.value - a.value);
  };
  
  // Cores para o gráfico de pizza
  const CHART_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658'];
  
  // Calcular forecast de custos baseado no checklist
  const calculateForecast = (): number => {
    if (!visit || !visit.checklist || visit.checklist.length === 0) {
      console.log('Forecast: Nenhum checklist encontrado');
      return 0;
    }
    
    console.log('Forecast: Checklist completo:', visit.checklist);
    console.log('Forecast: Total de itens no checklist:', visit.checklist.length);
    
    // Somar os valores dos itens do checklist que têm valor definido
    const forecast = visit.checklist.reduce((total, item, index) => {
      console.log(`Forecast: Item ${index}:`, {
        description: item.description,
        value: item.value,
        type: typeof item.value
      });
      
      if (item.value && item.value.trim() !== '') {
        // Tentar converter o valor para número
        const cleanValue = item.value.replace(',', '.');
        const numValue = parseFloat(cleanValue);
        console.log(`Forecast: Item ${index} - valor limpo: "${cleanValue}", convertido: ${numValue}, isNaN: ${isNaN(numValue)}`);
        
        if (!isNaN(numValue)) {
          console.log(`Forecast: Item ${index} - adicionando ${numValue} ao total ${total}`);
          return total + numValue;
        }
      }
      return total;
    }, 0);
    
    console.log('Forecast: Total calculado:', forecast);
    return forecast;
  };
  
  // Manipuladores para exclusão
  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteVisit = async () => {
    if (!id) return;
    
    try {
      await commercialVisitService.deleteCommercialVisit(id);
      showSnackbar('Visita comercial excluída com sucesso!', 'success');
      navigate('/commercial-visits');
    } catch (error) {
      console.error('Erro ao excluir visita comercial:', error);
      showSnackbar('Erro ao excluir visita comercial. Tente novamente.', 'error');
    } finally {
      handleCloseDeleteDialog();
    }
  };
  
  // Manipuladores para adição de ação
  const handleOpenActionDialog = () => {
    setActionDialogOpen(true);
  };

  const handleCloseActionDialog = () => {
    setActionDialogOpen(false);
    setNewAction('');
  };

  const handleAddAction = async () => {
    if (!id || !newAction.trim()) return;
    
    try {
      setAddingAction(true);
      await commercialVisitService.addAction(id, { description: newAction });
      
      // Recarregar visita para mostrar a nova ação
      const updatedVisit = await commercialVisitService.getCommercialVisitById(id);
      setVisit(updatedVisit);
      
      showSnackbar('Ação adicionada com sucesso!', 'success');
      handleCloseActionDialog();
    } catch (error) {
      console.error('Erro ao adicionar ação:', error);
      showSnackbar('Erro ao adicionar ação. Tente novamente.', 'error');
    } finally {
      setAddingAction(false);
    }
  };
  
  // Renderizar custos relacionados
  const renderCosts = () => {
    if (loadingCosts) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    
    if (costs.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Nenhum custo registrado para esta visita.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/costs/new?commercialVisit=${id}`)}
            sx={{ mt: 1 }}
          >
            Adicionar Custo
          </Button>
        </Box>
      );
    }
    
    // Calcular o total novamente para garantir que está correto
    const calculatedTotal = costs.reduce((sum, cost) => sum + (cost.amount || 0), 0);
    
    return (
      <Box>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Descrição</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Data</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {costs.map((cost) => (
                <TableRow key={cost._id} hover>
                  <TableCell>{cost.description}</TableCell>
                  <TableCell>{COST_CATEGORY_LABELS[cost.category]}</TableCell>
                  <TableCell>{new Date(cost.date).toLocaleDateString('pt-BR')}</TableCell>
                  <TableCell align="right">{costService.formatCurrency(cost.amount || 0)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver detalhes">
                      <IconButton 
                        size="small"
                        onClick={() => navigate(`/costs/${cost._id}`)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {/* Linha de total */}
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell colSpan={3} align="right">
                  <Typography variant="subtitle2" fontWeight="bold">Total:</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle1" color="primary" fontWeight="bold">
                    {costService.formatCurrency(calculatedTotal)}
                  </Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/costs/new?commercialVisit=${id}`)}
          >
            Adicionar Custo
          </Button>
        </Box>
      </Box>
    );
  };
  
  // Renderizar ações
  const renderActions = () => {
    if (!visit || !visit.actions || visit.actions.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Nenhuma ação registrada para esta visita.
          </Typography>
        </Box>
      );
    }
    
    return (
      <List dense>
        {visit.actions.map((action, index) => (
          <ListItem key={index} alignItems="flex-start">
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    label={action.status} 
                    size="small" 
                    color={
                      action.status === 'success' ? 'success' :
                      action.status === 'warning' ? 'warning' :
                      action.status === 'error' ? 'error' :
                      'default'
                    }
                  />
                  <Typography variant="body2" component="span">
                    {new Date(action.timestamp).toLocaleString('pt-BR')}
                  </Typography>
                </Box>
              }
              secondary={
                <React.Fragment>
                  <Typography component="span" variant="body2">
                    {action.description}
                  </Typography>
                  {typeof action.performedBy !== 'string' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Avatar 
                        src={action.performedBy?.photoURL} 
                        alt={action.performedBy?.displayName || 'Usuário'}
                        sx={{ width: 24, height: 24, mr: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {action.performedBy?.displayName || 'Usuário'}
                      </Typography>
                    </Box>
                  )}
                </React.Fragment>
              }
            />
          </ListItem>
        ))}
      </List>
    );
  };
  
  if (loading) {
    return (
      <AuthLayout title="Carregando Visita...">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </AuthLayout>
    );
  }
  
  if (!visit) {
    return (
      <Box>
        <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
          Visita comercial não encontrada
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/commercial-visits')}
        >
          Voltar para a lista
        </Button>
      </Box>
    );
  }
  
  return (
    <AuthLayout title="Detalhes da Visita Comercial">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1">
            Visita Comercial: {visit.number}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {visit.description}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/commercial-visits')}
            sx={{ mr: 1 }}
          >
            Voltar
          </Button>
          
          <Tooltip title="Editar">
            <IconButton 
              color="primary"
              onClick={() => navigate(`/commercial-visits/${visit._id}/edit`)}
              sx={{ mr: 1 }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Excluir">
            <IconButton 
              color="error"
              onClick={handleOpenDeleteDialog}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon color="primary" sx={{ mr: 2 }} />
              <Typography variant="h6">
                Informações da Visita
              </Typography>
            </Box>
            
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DescriptionIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1">
                    Descrição
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 4 }}>
                  {visit.description}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1">
                    Cliente
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 4 }}>
                  {typeof visit.client !== 'string' ? visit.client.name : ''}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CategoryIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1">
                    Tipo de Visita
                  </Typography>
                </Box>
                <Box sx={{ ml: 4 }}>
                  <Chip 
                    label={COMMERCIAL_VISIT_TYPE_LABELS[visit.type as keyof typeof COMMERCIAL_VISIT_TYPE_LABELS]} 
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DateRangeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1">
                    Data Agendada
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 4 }}>
                  {new Date(visit.scheduledDate).toLocaleDateString('pt-BR')}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CategoryIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1">
                    Status
                  </Typography>
                </Box>
                <Box sx={{ ml: 4 }}>
                  <Chip 
                    label={COMMERCIAL_VISIT_STATUS_LABELS[visit.status as keyof typeof COMMERCIAL_VISIT_STATUS_LABELS]} 
                    color={
                      visit.status === 'completed' ? 'success' :
                      visit.status === 'in_progress' ? 'primary' :
                      visit.status === 'scheduled' ? 'info' :
                      'default'
                    }
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CategoryIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1">
                    Prioridade
                  </Typography>
                </Box>
                <Box sx={{ ml: 4 }}>
                  <Chip 
                    label={COMMERCIAL_VISIT_PRIORITY_LABELS[visit.priority as keyof typeof COMMERCIAL_VISIT_PRIORITY_LABELS]} 
                    color={
                      visit.priority === 'critical' ? 'error' :
                      visit.priority === 'high' ? 'warning' :
                      visit.priority === 'medium' ? 'info' :
                      'default'
                    }
                    variant="outlined"
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1">
                    Responsável
                  </Typography>
                </Box>
                <Box sx={{ ml: 4, display: 'flex', alignItems: 'center' }}>
                  {typeof visit.assignedTo !== 'string' && (
                    <>
                      <Avatar 
                        src={visit.assignedTo?.photoURL} 
                        alt={visit.assignedTo?.displayName || 'Usuário'}
                        sx={{ width: 24, height: 24, mr: 1 }}
                      />
                      <Typography variant="body1">
                        {visit.assignedTo?.displayName || 'Usuário'}
                      </Typography>
                    </>
                  )}
                </Box>
              </Grid>
              
              {visit.opportunityValue && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AttachMoneyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle1">
                      Valor da Oportunidade
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="primary" sx={{ ml: 4 }}>
                    {costService.formatCurrency(visit.opportunityValue)}
                  </Typography>
                </Grid>
              )}
              
              {visit.location && visit.location.address && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationOnIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle1">
                      Endereço
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ ml: 4 }}>
                    {visit.location.address}
                  </Typography>
                  
                  {/* Mapa do Google Maps se houver coordenadas */}
                  {visit.location.latitude && visit.location.longitude && (
                    <Box sx={{ mt: 2 }}>
                      <GoogleMap
                        latitude={visit.location.latitude}
                        longitude={visit.location.longitude}
                        address={visit.location.address}
                        height={400}
                      />
                    </Box>
                  )}
                </Grid>
              )}
              
              {visit.notes && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <NotesIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="subtitle1">
                      Observações
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ ml: 4 }}>
                    {visit.notes}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
        
        {/* Coluna Lateral - Cards Informativos */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Card de Status da Visita */}
          <Card sx={{ border: '2px solid', borderColor: 'primary.main', flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" color="primary">
                  Status da Visita
                </Typography>
                <Chip 
                  label={COMMERCIAL_VISIT_STATUS_LABELS[visit.status as keyof typeof COMMERCIAL_VISIT_STATUS_LABELS]}
                  color={
                    visit.status === 'completed' ? 'success' :
                    visit.status === 'in_progress' ? 'warning' :
                    visit.status === 'cancelled' ? 'error' : 'info'
                  }
                  size="small"
                />
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DateRangeIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    Data Agendada
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 4, fontWeight: 'bold' }}>
                  {new Date(visit.scheduledDate).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
              
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CategoryIcon fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
                  <Typography variant="body2" color="text.secondary">
                    Prioridade
                  </Typography>
                </Box>
                <Chip 
                  label={COMMERCIAL_VISIT_PRIORITY_LABELS[visit.priority as keyof typeof COMMERCIAL_VISIT_PRIORITY_LABELS]}
                  color={
                    visit.priority === 'critical' ? 'error' :
                    visit.priority === 'high' ? 'warning' :
                    visit.priority === 'medium' ? 'info' : 'default'
                  }
                  size="small"
                  sx={{ ml: 4 }}
                />
              </Box>
            </CardContent>
          </Card>
          
          {/* Card de Total de Custos */}
          <Card sx={{ bgcolor: 'success.light', flex: 1 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Total de Custos da Visita
              </Typography>
              <Typography variant="h3" color="success.dark" fontWeight="bold">
                {totalCost ? costService.formatCurrency(totalCost) : 'R$ 0,00'}
              </Typography>
              {costs.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {costs.length} {costs.length === 1 ? 'item registrado' : 'itens registrados'}
                </Typography>
              )}
            </CardContent>
          </Card>
          
          {/* Card de Previsão de Custos */}
          <Card sx={{ border: '2px solid', borderColor: 'warning.main', flex: 1 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Forecast de Custos
              </Typography>
              <Typography variant="h3" color="warning.dark" fontWeight="bold">
                {costService.formatCurrency(calculateForecast())}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {visit.checklist && visit.checklist.length > 0 
                  ? `${visit.checklist.filter(item => item.value && item.value.trim() !== '').length} itens com valor no checklist`
                  : 'Nenhum item no checklist'}
              </Typography>
            </CardContent>
          </Card>
          
          {/* Card de Distribuição de Custos */}
          <Card sx={{ border: '1px solid', borderColor: 'divider', flex: 1 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ textAlign: 'center' }}>
                Distribuição dos Principais Custos
              </Typography>
              <Divider sx={{ my: 2 }} />
              {costs.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={prepareCostDistributionData()}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percentage }) => `${percentage}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {prepareCostDistributionData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  
                  {/* Legenda com valores */}
                  <Box sx={{ mt: 2 }}>
                    {prepareCostDistributionData().map((item, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          display: 'grid',
                          gridTemplateColumns: 'auto 1fr auto',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                          p: 1,
                          bgcolor: 'grey.50',
                          borderRadius: 1
                        }}
                      >
                        <Box 
                          sx={{ 
                            width: 12, 
                            height: 12, 
                            bgcolor: CHART_COLORS[index % CHART_COLORS.length],
                            borderRadius: '50%'
                          }} 
                        />
                        <Typography variant="caption">
                          {item.name}
                        </Typography>
                        <Typography variant="caption" fontWeight="bold" sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {costService.formatCurrency(item.value)} ({item.percentage}%)
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  Não há custos registrados para exibir no gráfico.
                </Typography>
              )}
            </CardContent>
          </Card>
          </Box>
        </Grid>
      </Grid>
      
      {/* Tabs para Ações/Histórico e Custos */}
      <Paper sx={{ mt: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<HistoryIcon />} 
            iconPosition="start" 
            label="Ações e Histórico" 
          />
          <Tab 
            icon={<ReceiptIcon />} 
            iconPosition="start" 
            label="Custos da Visita" 
          />
          <Tab 
            icon={<ChecklistIcon />} 
            iconPosition="start" 
            label="Checklist" 
          />
        </Tabs>
        
        {/* Aba 1: Ações e Histórico */}
        {activeTab === 0 && (
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Ações e Histórico
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenActionDialog}
              >
                Adicionar Ação
              </Button>
            </Box>
            {renderActions()}
          </Box>
        )}
        
        {/* Aba 2: Custos da Visita */}
        {activeTab === 1 && (
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 3, 
                    mb: 3, 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    bgcolor: 'primary.main',
                    color: 'white'
                  }}
                >
                  <Typography variant="subtitle1" gutterBottom>
                    Total de Custos da Visita
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {totalCost ? costService.formatCurrency(totalCost) : 'R$ 0,00'}
                  </Typography>
                  {costs.length > 0 && (
                    <Typography variant="caption" sx={{ mt: 1, opacity: 0.9 }}>
                      {costs.length} {costs.length === 1 ? 'custo registrado' : 'custos registrados'}
                    </Typography>
                  )}
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                {renderCosts()}
              </Grid>
            </Grid>
          </Box>
        )}
        
        {/* Aba 3: Checklist */}
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Checklist da Visita
            </Typography>
            
            {/* Resumo do Checklist */}
            {visit.checklist && visit.checklist.length > 0 && (
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.light' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Total de Itens
                    </Typography>
                    <Typography variant="h5" color="primary.dark" fontWeight="bold">
                      {visit.checklist.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Concluídos
                    </Typography>
                    <Typography variant="h5" color="success.dark" fontWeight="bold">
                      {visit.checklist.filter(item => item.isCompleted).length}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Progresso
                    </Typography>
                    <Typography variant="h5" color="primary.dark" fontWeight="bold">
                      {visit.checklist.length > 0 
                        ? Math.round((visit.checklist.filter(item => item.isCompleted).length / visit.checklist.length) * 100)
                        : 0}%
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}
            
            {visit.checklist && visit.checklist.length > 0 ? (
              <List>
                {visit.checklist.map((item, index) => (
                  <React.Fragment key={index}>
                    <ListItem
                      sx={{
                        bgcolor: item.isCompleted ? 'success.light' : 'grey.50',
                        mb: 1,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: item.isCompleted ? 'success.main' : 'divider'
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={item.isCompleted ? 'Concluído' : 'Pendente'}
                              color={item.isCompleted ? 'success' : 'default'}
                              size="small"
                            />
                            <Typography variant="body1" fontWeight="medium">
                              {item.description}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            {item.type && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Tipo: {item.type === 'simple_check' ? 'Verificação Simples' : 
                                       item.type === 'value_input' ? 'Entrada de Valor' :
                                       item.type === 'image_capture' ? 'Captura de Imagem' :
                                       item.type === 'collect_signature' ? 'Coleta de Assinatura' :
                                       item.type === 'update_data' ? 'Atualização de Dados' :
                                       item.type === 'update_equipment' ? 'Atualização de Equipamento' : item.type}
                              </Typography>
                            )}
                            {item.value && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Valor: {item.value}
                              </Typography>
                            )}
                            {item.completedAt && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Concluído em: {new Date(item.completedAt).toLocaleString('pt-BR')}
                              </Typography>
                            )}
                            {item.notes && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Observações: {item.notes}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <ChecklistIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Nenhum checklist associado
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Esta visita não possui itens de checklist cadastrados.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>
      
      {/* Informações do Sistema */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Informações do Sistema
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <List dense>
                <ListItem>
                  <ListItemText 
                    primary="ID" 
                    secondary={visit._id} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Criado por" 
                    // @ts-ignore - Propriedade displayName não existe no tipo string
                    secondary={visit.createdBy?.displayName || 'Usuário'} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Criado em" 
                    secondary={new Date(visit.createdAt).toLocaleString('pt-BR')} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemText 
                    primary="Última atualização" 
                    secondary={new Date(visit.updatedAt).toLocaleString('pt-BR')} 
                  />
                </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir esta visita comercial? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleDeleteVisit} color="error" autoFocus>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Diálogo de adição de ação */}
      <Dialog
        open={actionDialogOpen}
        onClose={handleCloseActionDialog}
      >
        <DialogTitle>Adicionar Ação</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Registre uma nova ação ou observação para esta visita comercial.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="action"
            label="Descrição da Ação"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={newAction}
            onChange={(e) => setNewAction(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActionDialog} color="primary">
            Cancelar
          </Button>
          <Button 
            onClick={handleAddAction} 
            color="primary" 
            disabled={!newAction.trim() || addingAction}
            startIcon={addingAction ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {addingAction ? 'Adicionando...' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>
    </AuthLayout>
  );
};

export default CommercialVisitDetail;
